import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Select,
  Option,
  Checkbox,
  Switch,
  Input,
  Stack,
  Chip,
  Card,
  CardContent,
  RadioGroup,
  Radio,
  Sheet,
  Alert
} from '@mui/joy';
import {
  Clock as ScheduleIcon,
  Robot as AIIcon,
  Folder as TopicIcon,
  Gear as SettingsIcon,
  Clock as TimeIcon,
  Calendar as CalendarIcon
} from '@phosphor-icons/react';
import type { MarketingSettings as BaseMarketingSettings } from '../../types/marketing';

interface LocalMarketingSettings {
  auto_generation_enabled: boolean;
  generation_frequency: string;
  posts_per_generation: number;
  posting_days: string[];
  posting_time_mode: string;
  specific_posting_time: string;
  random_posting_start: string;
  random_posting_end: string;
  auto_generation_topics: {
    educational_tips: boolean;
    service_highlights: boolean;
    seasonal_content: boolean;
    project_showcases: boolean;
    customer_testimonials: boolean;
    maintenance_reminders: boolean;
    local_events: boolean;
    industry_news: boolean;
  };
  custom_topics: string[];
  use_top_pages: boolean;
  use_marketing_images: boolean;
  require_review: boolean;
}

interface GBPAutoSettingsModalProps {
  open: boolean;
  onClose: () => void;
  settings: BaseMarketingSettings | null;
  onSave: (settings: Partial<BaseMarketingSettings>) => Promise<void>;
}

export const GBPAutoSettingsModal: React.FC<GBPAutoSettingsModalProps> = ({
  open,
  onClose,
  settings,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState('frequency');
  const [saving, setSaving] = useState(false);
  const [localSettings, setLocalSettings] = useState<Partial<LocalMarketingSettings>>({});

  useEffect(() => {
    if (settings) {
      const localSettings_temp = settings as any;
      setLocalSettings({
        generation_frequency: localSettings_temp.generation_frequency || 'weekly',
        posts_per_generation: localSettings_temp.posts_per_generation || 3,
        posting_days: localSettings_temp.posting_days || ['monday', 'wednesday', 'friday'],
        posting_time_mode: localSettings_temp.posting_time_mode || 'specific',
        specific_posting_time: localSettings_temp.specific_posting_time || '10:00',
        random_posting_start: localSettings_temp.random_posting_start || '09:00',
        random_posting_end: localSettings_temp.random_posting_end || '17:00',
        auto_generation_topics: localSettings_temp.auto_generation_topics || {
          educational_tips: true,
          service_highlights: true,
          seasonal_content: true,
          project_showcases: true,
          customer_testimonials: true,
          maintenance_reminders: true,
          local_events: false,
          industry_news: false,
        },
        custom_topics: localSettings_temp.custom_topics || [],
        use_top_pages: localSettings_temp.use_top_pages !== false,
        use_marketing_images: localSettings_temp.use_marketing_images !== false,
        require_review: localSettings_temp.require_review !== false,
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(localSettings as Partial<BaseMarketingSettings>);
    } finally {
      setSaving(false);
    }
  };

  const daysOfWeek = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' },
  ];

  const topicLabels = {
    educational_tips: 'Educational Tips',
    service_highlights: 'Service Highlights',
    seasonal_content: 'Seasonal Content',
    project_showcases: 'Project Showcases',
    customer_testimonials: 'Customer Testimonials',
    maintenance_reminders: 'Maintenance Reminders',
    local_events: 'Local Events',
    industry_news: 'Industry News',
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 700, width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h3" startDecorator={<AIIcon />}>
          Auto-Generation Settings
        </Typography>
        
        <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value as string)}>
          <TabList>
            <Tab value="frequency">
              <ScheduleIcon style={{ marginRight: 8 }} />
              Frequency
            </Tab>
            <Tab value="scheduling">
              <CalendarIcon style={{ marginRight: 8 }} />
              Scheduling
            </Tab>
            <Tab value="topics">
              <TopicIcon style={{ marginRight: 8 }} />
              Topics
            </Tab>
            <Tab value="review">
              <SettingsIcon style={{ marginRight: 8 }} />
              Review
            </Tab>
          </TabList>

          <TabPanel value="frequency">
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Generation Frequency</FormLabel>
                <Select
                  value={localSettings.generation_frequency}
                  onChange={(e, value) => 
                    setLocalSettings({ ...localSettings, generation_frequency: value as string })
                  }
                >
                  <Option value="daily">Daily</Option>
                  <Option value="weekly">Weekly</Option>
                  <Option value="biweekly">Twice Weekly</Option>
                  <Option value="custom">Custom Schedule</Option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Posts per Generation Batch</FormLabel>
                <Select
                  value={localSettings.posts_per_generation}
                  onChange={(e, value) =>
                    setLocalSettings({ ...localSettings, posts_per_generation: value as number })
                  }
                >
                  <Option value={1}>1 post</Option>
                  <Option value={3}>3 posts</Option>
                  <Option value={5}>5 posts</Option>
                  <Option value={7}>7 posts</Option>
                  <Option value={10}>10 posts</Option>
                </Select>
              </FormControl>

              <Alert color="primary" variant="soft">
                Based on your settings, you'll generate approximately{' '}
                <strong>
                  {localSettings.generation_frequency === 'daily'
                    ? localSettings.posts_per_generation! * 30
                    : localSettings.generation_frequency === 'weekly'
                    ? localSettings.posts_per_generation! * 4
                    : localSettings.generation_frequency === 'biweekly'
                    ? localSettings.posts_per_generation! * 8
                    : '???'}
                </strong>{' '}
                posts per month.
              </Alert>
            </Stack>
          </TabPanel>

          <TabPanel value="scheduling">
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Posting Days</FormLabel>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {daysOfWeek.map(day => (
                    <Chip
                      key={day.value}
                      variant={
                        localSettings.posting_days?.includes(day.value) ? 'solid' : 'outlined'
                      }
                      color={
                        localSettings.posting_days?.includes(day.value) ? 'primary' : 'neutral'
                      }
                      onClick={() => {
                        const days = localSettings.posting_days || [];
                        if (days.includes(day.value)) {
                          setLocalSettings({
                            ...localSettings,
                            posting_days: days.filter((d: string) => d !== day.value)
                          });
                        } else {
                          setLocalSettings({
                            ...localSettings,
                            posting_days: [...days, day.value]
                          });
                        }
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      {day.label}
                    </Chip>
                  ))}
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>Posting Time</FormLabel>
                <RadioGroup
                  value={localSettings.posting_time_mode}
                  onChange={(event) =>
                    setLocalSettings({ ...localSettings, posting_time_mode: event.target.value })
                  }
                >
                  <Radio value="specific" label="Specific Time" />
                  <Radio value="random" label="Random Time Window" />
                </RadioGroup>
              </FormControl>

              {localSettings.posting_time_mode === 'specific' ? (
                <FormControl>
                  <FormLabel>Post at Time</FormLabel>
                  <Input
                    type="time"
                    value={localSettings.specific_posting_time}
                    onChange={(e) =>
                      setLocalSettings({ ...localSettings, specific_posting_time: e.target.value })
                    }
                    startDecorator={<TimeIcon />}
                  />
                </FormControl>
              ) : (
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>Random Window Start</FormLabel>
                    <Input
                      type="time"
                      value={localSettings.random_posting_start}
                      onChange={(e) =>
                        setLocalSettings({ ...localSettings, random_posting_start: e.target.value })
                      }
                      startDecorator={<TimeIcon />}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Random Window End</FormLabel>
                    <Input
                      type="time"
                      value={localSettings.random_posting_end}
                      onChange={(e) =>
                        setLocalSettings({ ...localSettings, random_posting_end: e.target.value })
                      }
                      startDecorator={<TimeIcon />}
                    />
                  </FormControl>
                </Stack>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value="topics">
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Content Topics</FormLabel>
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                  <Stack spacing={1.5}>
                    {Object.entries(topicLabels).map(([key, label]) => (
                      <Checkbox
                        key={key}
                        label={label}
                        checked={localSettings.auto_generation_topics?.[key as keyof typeof localSettings.auto_generation_topics] || false}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            auto_generation_topics: {
                              ...localSettings.auto_generation_topics,
                              [key]: e.target.checked
                            } as LocalMarketingSettings['auto_generation_topics']
                          })
                        }
                      />
                    ))}
                  </Stack>
                </Sheet>
              </FormControl>

              <FormControl>
                <FormLabel>Content Sources</FormLabel>
                <Stack spacing={2}>
                  <Checkbox
                    label="Include links to Top Pages (from Organization Settings)"
                    checked={localSettings.use_top_pages}
                    onChange={(e) =>
                      setLocalSettings({ ...localSettings, use_top_pages: e.target.checked })
                    }
                  />
                  <Checkbox
                    label="Use images tagged 'marketing' from photo library"
                    checked={localSettings.use_marketing_images}
                    onChange={(e) =>
                      setLocalSettings({ ...localSettings, use_marketing_images: e.target.checked })
                    }
                  />
                </Stack>
              </FormControl>

              <FormControl>
                <FormLabel>Custom Topics (comma-separated)</FormLabel>
                <Input
                  placeholder="e.g., Spring cleaning, Storm preparation, Energy efficiency"
                  value={localSettings.custom_topics?.join(', ') || ''}
                  onChange={(e) =>
                    setLocalSettings({
                      ...localSettings,
                      custom_topics: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                    })
                  }
                />
              </FormControl>
            </Stack>
          </TabPanel>

          <TabPanel value="review">
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Review Settings</FormLabel>
                <Stack spacing={2}>
                  <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                    <FormLabel>Require review before publishing</FormLabel>
                    <Switch
                      checked={localSettings.require_review}
                      onChange={(e) =>
                        setLocalSettings({ ...localSettings, require_review: e.target.checked })
                      }
                    />
                  </FormControl>
                </Stack>
              </FormControl>

              {localSettings.require_review ? (
                <Alert color="primary" variant="soft">
                  Generated posts will be saved as "Pending Review" and require manual approval
                  before they can be published to Google Business Profile.
                </Alert>
              ) : (
                <Alert color="warning" variant="soft">
                  Generated posts will be automatically published without review. Make sure your
                  content generation settings are properly configured.
                </Alert>
              )}

              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>Summary</Typography>
                  <Typography level="body-sm">
                    • Generate <strong>{localSettings.posts_per_generation}</strong> posts{' '}
                    <strong>{localSettings.generation_frequency}</strong>
                    <br />
                    • Post on: <strong>{localSettings.posting_days?.join(', ')}</strong>
                    <br />
                    • Time: {localSettings.posting_time_mode === 'specific' ? (
                      <strong>{localSettings.specific_posting_time}</strong>
                    ) : (
                      <strong>{localSettings.random_posting_start} - {localSettings.random_posting_end}</strong>
                    )}
                    <br />
                    • Review required: <strong>{localSettings.require_review ? 'Yes' : 'No'}</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </TabPanel>
        </Tabs>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" onClick={handleSave} loading={saving}>
            Save Settings
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};